import { useState } from 'react';
import LegalNavbar from '../../components/feature/LegalNavbar';
import LegalFooter from '../../components/feature/LegalFooter';

const COOKIE_TYPES = [
  {
    icon: 'ri-settings-4-line',
    name: 'Cookies Técnicas o Esenciales',
    required: true,
    description: 'Son imprescindibles para el funcionamiento básico del sitio web. Sin ellas, el sitio web no puede funcionar correctamente. No requieren consentimiento del usuario.',
    examples: [
      { name: 'session_id', duration: 'Sesión', purpose: 'Mantiene la sesión activa del usuario.' },
      { name: 'csrf_token', duration: 'Sesión', purpose: 'Protección contra ataques de falsificación de solicitudes.' },
      { name: 'cookie_consent', duration: '1 año', purpose: 'Almacena las preferencias de cookies del usuario.' },
    ],
  },
  {
    icon: 'ri-bar-chart-line',
    name: 'Cookies Analíticas',
    required: false,
    description: 'Permiten cuantificar el número de usuarios y analizar estadísticamente cómo se utiliza el sitio web. Nos ayudan a mejorar la experiencia de navegación.',
    examples: [
      { name: '_ga', duration: '2 años', purpose: 'Google Analytics: distingue usuarios únicos.' },
      { name: '_gid', duration: '24 horas', purpose: 'Google Analytics: distingue usuarios por sesión.' },
      { name: '_gat', duration: '1 minuto', purpose: 'Google Analytics: limita el porcentaje de solicitudes.' },
    ],
  },
  {
    icon: 'ri-share-line',
    name: 'Cookies de Redes Sociales',
    required: false,
    description: 'Utilizadas por plataformas de redes sociales para permitir compartir contenido y hacer seguimiento de interacciones cuando el usuario hace clic en botones de compartir.',
    examples: [
      { name: 'ln_or', duration: '1 día', purpose: 'LinkedIn: redirección de campaña.' },
      { name: 'lidc', duration: '1 día', purpose: 'LinkedIn: enrutamiento del servidor.' },
    ],
  },
];

const SECTIONS_AFTER = [
  {
    title: '¿Cómo puedo gestionar o deshabilitar las cookies?',
    content: (
      <div className="space-y-3 text-gray-600 text-sm leading-relaxed">
        <p>Puede configurar su navegador para bloquear o eliminar las cookies. A continuación le indicamos cómo hacerlo en los principales navegadores:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><strong>Google Chrome:</strong> Menú &gt; Configuración &gt; Privacidad y seguridad &gt; Cookies y otros datos de sitios.</li>
          <li><strong>Mozilla Firefox:</strong> Menú &gt; Opciones &gt; Privacidad y seguridad &gt; Cookies y datos del sitio.</li>
          <li><strong>Safari:</strong> Preferencias &gt; Privacidad &gt; Gestión de datos del sitio web.</li>
          <li><strong>Microsoft Edge:</strong> Menú &gt; Configuración &gt; Privacidad, búsqueda y servicios &gt; Cookies.</li>
        </ul>
        <p className="mt-3">Tenga en cuenta que deshabilitar ciertas cookies puede afectar al funcionamiento del sitio web.</p>
      </div>
    ),
  },
  {
    title: 'Cookies de terceros',
    content: (
      <p className="text-gray-600 text-sm leading-relaxed">
        Este sitio web puede utilizar servicios de terceros (como Google Analytics o plataformas de redes sociales) que instalan sus propias cookies. El titular no tiene control sobre estas cookies una vez instaladas. Para más información sobre las cookies de terceros, consulte sus respectivas políticas de privacidad. En particular, puede consultar la <a href="https://policies.google.com/privacy" rel="nofollow" className="text-[#3D9E8C] hover:underline">Política de Privacidad de Google</a>.
      </p>
    ),
  },
  {
    title: 'Actualizaciones de la Política de Cookies',
    content: (
      <p className="text-gray-600 text-sm leading-relaxed">
        El titular se reserva el derecho a modificar la presente Política de Cookies para adaptarla a novedades legislativas o cambios en la actividad del Sitio Web. En caso de cambios significativos, se informará al usuario mediante un aviso en el Sitio Web. Se recomienda revisar esta política periódicamente.
      </p>
    ),
  },
];

export default function Cookies() {
  const [accepted, setAccepted] = useState<Record<string, boolean>>({
    analytics: true,
    social: false,
  });

  const toggle = (key: string) => {
    setAccepted((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-[#F4F7FA] flex flex-col">
      <LegalNavbar />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-[#1B3A5C] py-14">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#3D9E8C] mb-3">Información Legal</span>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-3">Política de Cookies</h1>
            <p className="text-white/60 text-sm">Última actualización: 23 de marzo de 2026</p>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-4xl mx-auto px-6 space-y-6">

            {/* Intro */}
            <div className="bg-white rounded-lg px-8 py-7">
              <h4 className="font-semibold text-[#1B3A5C] text-base mb-4">
                <a id="cookies-intro" href="#cookies-intro">¿Qué son las cookies?</a>
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Sirven para que el sitio web recuerde sus preferencias, analice el uso del servicio y, en algunos casos, muestre publicidad personalizada. La presente Política de Cookies ha sido elaborada conforme a la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información (LSSI), el RGPD y la Guía sobre el uso de las cookies de la AEPD.
              </p>
            </div>

            {/* Cookie types */}
            {COOKIE_TYPES.map((type, i) => {
              const key = type.name.toLowerCase().includes('analítica') ? 'analytics' : type.name.toLowerCase().includes('social') ? 'social' : '';
              return (
                <div key={i} className="bg-white rounded-lg overflow-hidden">
                  <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1B3A5C]/8 flex-shrink-0">
                        <i className={`${type.icon} text-[#1B3A5C] text-base`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1B3A5C] text-sm">{type.name}</h4>
                        {type.required && (
                          <span className="text-xs text-[#3D9E8C] font-medium">Siempre activas</span>
                        )}
                      </div>
                    </div>
                    {!type.required && key && (
                      <button
                        onClick={() => toggle(key)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
                          accepted[key] ? 'bg-[#3D9E8C]' : 'bg-gray-200'
                        }`}
                        aria-label={`Toggle ${type.name}`}
                      >
                        <span
                          className={`inline-block w-3.5 h-3.5 rounded-full bg-white transition-transform duration-200 ${
                            accepted[key] ? 'translate-x-4' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    )}
                    {type.required && (
                      <div className="w-9 h-5 rounded-full bg-[#3D9E8C] flex items-center justify-end pr-0.5 flex-shrink-0">
                        <span className="w-3.5 h-3.5 rounded-full bg-white block" />
                      </div>
                    )}
                  </div>
                  <div className="px-8 py-5">
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{type.description}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left font-semibold text-[#1B3A5C] pb-2 pr-4">Nombre</th>
                            <th className="text-left font-semibold text-[#1B3A5C] pb-2 pr-4">Duración</th>
                            <th className="text-left font-semibold text-[#1B3A5C] pb-2">Propósito</th>
                          </tr>
                        </thead>
                        <tbody>
                          {type.examples.map((ex, j) => (
                            <tr key={j} className="border-b border-gray-50 last:border-0">
                              <td className="py-2 pr-4 font-mono text-gray-500 whitespace-nowrap">{ex.name}</td>
                              <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{ex.duration}</td>
                              <td className="py-2 text-gray-500">{ex.purpose}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Additional sections */}
            <div className="bg-white rounded-lg overflow-hidden">
              {SECTIONS_AFTER.map((section, i) => (
                <div key={i} className={`px-8 py-7 ${i !== SECTIONS_AFTER.length - 1 ? 'border-b border-gray-100' : ''}`}>
                  <h4 className="font-semibold text-[#1B3A5C] text-base mb-4">
                    <a id={`cookies-seccion-${i + 1}`} href={`#cookies-seccion-${i + 1}`}>{section.title}</a>
                  </h4>
                  {section.content}
                </div>
              ))}
            </div>

            {/* Contact box */}
            <div className="bg-[#1B3A5C]/5 border border-[#1B3A5C]/10 rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#3D9E8C]/15 flex-shrink-0">
                <i className="ri-mail-line text-[#3D9E8C] text-lg" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1B3A5C]">¿Tienes dudas sobre el uso de cookies?</p>
                <p className="text-xs text-gray-500 mt-0.5">Contáctanos en <a href="mailto:jlarrache@mediatioglobal.com" className="text-[#3D9E8C] hover:underline">jlarrache@mediatioglobal.com</a> o llama al <a href="tel:+34696342047" className="text-[#3D9E8C] hover:underline">(+34) 696 342 047</a></p>
              </div>
            </div>

          </div>
        </section>
      </main>
      <LegalFooter />
    </div>
  );
}
